import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { tileSize, meshSize } from "@/lib/dims";

const newKey = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `k${Date.now()}${Math.random()}`);

// ID-060: + New Color — one master color → the four canonical format variants
export function NewColorDialog({ open, onOpenChange, onCreated }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [us, setUs] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [key, setKey] = useState(newKey);
  useEffect(() => {
    if (!open) { return; }
    setKey(newKey()); setName(""); setUs("");
    api.get("/admin/catalog/master-colors/next").then(({ data }) => { setCode(data.code); setPreview(data); });
  }, [open]);
  useEffect(() => {
    if (!open || !/^\d{3}$/.test(code)) { setPreview(null); return; }
    api.get(`/admin/catalog/master-colors/preview/${code}`).then(({ data }) => setPreview(data)).catch(() => setPreview(null));
  }, [code, open]);
  const blocked = !preview?.valid || preview?.color_exists || preview?.variants?.some((v) => v.exists);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/catalog/master-colors", { code, moroccan_name: name, us_name: us || null, idempotency_key: key });
      toast.success(`Color ${data.code} — ${name.toUpperCase()} created with ${data.skus.join(", ")} (inactive until priced and approved)`);
      onOpenChange(false); onCreated?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-2xl" data-testid="new-color-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium text-charcoal">New master color</DialogTitle>
          <DialogDescription className="text-sm text-warmgrey">Creates the master color, a draft Color Governance record and its four canonical variants (4×4, 2×6, 2×2, TALIA) with specifications from the format registry. Nothing becomes sellable automatically.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Color code *</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 3))} data-testid="new-color-code" className="h-10 bg-white" /></div>
          <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Moroccan factory name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="new-color-moroccan" className="h-10 bg-white" /></div>
          <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">U.S. name (optional)</Label>
            <Input value={us} onChange={(e) => setUs(e.target.value)} placeholder="Pending" data-testid="new-color-us" className="h-10 bg-white" /></div>
        </div>
        <p className="text-sm text-charcoal" data-testid="new-color-label">{code || "—"} — {name.toUpperCase() || "…"} · {us ? `${us.toUpperCase()} (draft — awaits approval)` : "U.S. name pending"}</p>
        {preview?.color_exists && <p className="text-xs text-status-reserved" data-testid="new-color-exists">Master color {code} already exists.</p>}
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full min-w-[560px] text-xs" data-testid="new-color-preview">
            <thead><tr className="border-b border-border text-left text-warmgrey">{["SKU", "Format", "Size", "Presentation", "Pcs/box", "Sq ft/box", ""].map((h) => <th key={h} className="px-2 py-2 font-medium">{h}</th>)}</tr></thead>
            <tbody>{(preview?.variants || []).map((v) => (
              <tr key={v.sku} className="border-b border-border/60" data-testid={`new-color-sku-${v.sku}`}>
                <td className="px-2 py-2 font-mono">{v.sku}</td><td className="px-2 py-2">{v.label}</td><td className="px-2 py-2">{tileSize(v) || "—"}</td>
                <td className="px-2 py-2">{v.presentation}{meshSize(v) ? ` · ${meshSize(v)} mesh` : ""}</td>
                <td className="px-2 py-2">{v.pieces_per_box}</td><td className="px-2 py-2">{v.box_coverage_sqft}</td>
                <td className="px-2 py-2 text-status-reserved">{v.exists ? "SKU exists" : ""}</td>
              </tr>))}</tbody>
          </table>
        </div>
        <Button disabled={busy || blocked || !name.trim()} onClick={submit} data-testid="new-color-submit" className="h-11 bg-terracotta text-white hover:bg-terracotta-hover">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create color and 4 variants"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
